<template>
  <div class="radar-page">
    <!-- 返回按钮 -->
    <div class="back-button-container">
      <el-button 
        type="text" 
        @click="goBack"
        class="back-btn"
        icon="el-icon-arrow-left"
      >
        返回
      </el-button>
    </div>
    
    <!-- 配置面板 -->
    <config-panel 
      @config-change="handleConfigChange" 
      @reset="handleReset" 
      @major-change="handleMajorChange"
      @ai-majors-update="handleAIMajorsUpdate"
    />
    
    <div class="header-section">
      <h2 class="radar-title">专业匹配雷达图</h2>
      <el-button 
        type="primary" 
        size="small" 
        @click="goToSankey"
        class="sankey-btn"
      >
        查看桑基图分析
      </el-button>
    </div>
    
    <div class="legend-info">
      <div class="legend-item">
        <span class="legend-box personal"></span>
        <span>个人画像</span>
      </div>
      <div class="legend-item">
        <span class="legend-box major"></span>
        <span>{{ currentMajor?.name || '专业名称' }}</span>
      </div>
    </div>
    
    <div class="radar-container">
      <div ref="radarChart" class="chart-wrapper"></div>
    </div>
    
    <!-- 右弹窗 -->
    <right-detail-panel 
      :selected-major="currentMajorId"
      :display-majors="majors"
      @detail-loaded="handleDetailLoaded"
      ref="rightPanel"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts'
import ConfigPanel from './ConfigPanel.vue'
import RightDetailPanel from './RightDetailPanel.vue'

export default {
  name: 'RadarView',
  components: {
    ConfigPanel,
    RightDetailPanel
  },
  data () {
    return {
      radarChart: null,
      currentMajorId: 1,
      majors: [
        {
          id: 1,
          name: '电子信息工程',
          radarData: {
            studyAdaptability: 75,
            interestFit: 70,
            resourceMatch: 65,
            regionFit: 80,
            riskFit: 45
          }
        },
        {
          id: 2,
          name: '汉语言文学（师范）',
          radarData: {
            studyAdaptability: 65,
            interestFit: 85,
            resourceMatch: 70,
            regionFit: 75,
            riskFit: 80
          }
        },
        {
          id: 3,
          name: '自动化',
          radarData: {
            studyAdaptability: 80,
            interestFit: 65,
            resourceMatch: 60,
            regionFit: 70,
            riskFit: 55
          }
        }
      ],
      personalProfile: {
        studyAdaptability: 80,
        interestFit: 65,
        resourceMatch: 70,
        regionFit: 75,
        riskFit: 60
      }
    }
  },
  computed: {
    currentMajor () {
      return this.majors.find(m => m.id === this.currentMajorId) || this.majors[0]
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initRadarChart()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.radarChart) {
      this.radarChart.dispose()
    }
  },
  methods: {
    initRadarChart () {
      if (!this.$refs.radarChart) return
      
      this.radarChart = echarts.init(this.$refs.radarChart)
      
      const option = {
        tooltip: {
          trigger: 'item'
        },
        radar: {
          indicator: [
            { name: '学习适配度', max: 100 },
            { name: '兴趣契合度', max: 100 },
            { name: '资源匹配度', max: 100 },
            { name: '地域适配度', max: 100 },
            { name: '风险适配度', max: 100 }
          ],
          splitNumber: 5,
          center: ['50%', '55%'],
          radius: '45%',
          splitArea: {
            areaStyle: {
              color: ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 0.05)']
            }
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(211, 211, 211, 0.8)'
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(211, 211, 211, 0.8)'
            }
          }
        },
        series: [
          {
            type: 'radar',
            data: [
              {
                value: [
                  this.personalProfile.studyAdaptability,
                  this.personalProfile.interestFit,
                  this.personalProfile.resourceMatch,
                  this.personalProfile.regionFit,
                  this.personalProfile.riskFit
                ],
                name: '个人画像',
                areaStyle: {
                  color: 'rgba(59, 130, 246, 0.2)'
                },
                lineStyle: {
                  color: 'rgba(59, 130, 246, 1)',
                  width: 2
                },
                itemStyle: {
                  color: 'rgba(59, 130, 246, 1)'
                }
              },
            {
              value: [
                this.currentMajor?.radarData?.studyAdaptability || 50,
                this.currentMajor?.radarData?.interestFit || 50,
                this.currentMajor?.radarData?.resourceMatch || 50,
                this.currentMajor?.radarData?.regionFit || 50,
                this.currentMajor?.radarData?.riskFit || 50
              ],
              name: this.currentMajor?.name || '专业名称',
                areaStyle: {
                  color: 'rgba(239, 68, 68, 0.2)'
                },
                lineStyle: {
                  color: 'rgba(239, 68, 68, 1)',
                  width: 2,
                  type: 'dashed'
                },
                itemStyle: {
                  color: 'rgba(239, 68, 68, 1)'
                }
              }
            ]
          }
        ]
      }
      
      this.radarChart.setOption(option)
    },
    handleResize () {
      if (this.radarChart) {
        this.radarChart.resize()
      }
    },
    handleConfigChange (config) {
      // 根据配置更新个人画像数据
      console.log('配置变更:', config)
      
      // 根据表单数据计算个人画像分数
      this.calculatePersonalProfile(config)
      this.updateRadarChart()
    },
    
    calculatePersonalProfile(formData) {
      // 基于表单数据计算个人画像的各维度分数
      let studyScore = 50 // 基础分数
      let interestScore = 50
      let resourceScore = 50
      let regionScore = 50
      let riskScore = 50
      
      // 根据分数段调整学习适配度
      if (formData.scoreRange) {
        const scoreRangeMap = {
          '400-450': 40,
          '450-500': 50,
          '500-550': 65,
          '550-600': 80,
          '600+': 95
        }
        studyScore = scoreRangeMap[formData.scoreRange] || 50
      }
      
      // 根据优势学科调整学习适配度
      if (formData.subjects && formData.subjects.length > 0) {
        studyScore += Math.min(formData.subjects.length * 5, 25)
      }
      
      // 根据职业兴趣类型调整兴趣契合度
      if (formData.careerType) {
        const careerTypeMap = {
          'realistic': 60,
          'investigative': 85,
          'artistic': 70,
          'social': 75,
          'enterprising': 80,
          'conventional': 65
        }
        interestScore = careerTypeMap[formData.careerType] || 50
      }
      
      // 根据家庭资源调整资源匹配度
      if (formData.familyResource) {
        resourceScore = 70
      }
      if (formData.economicSupport) {
        const economicMap = {
          'high': 85,
          'medium': 65,
          'low': 45
        }
        resourceScore = Math.max(resourceScore, economicMap[formData.economicSupport] || 50)
      }
      
      // 根据目标就业地调整地域适配度
      if (formData.targetRegions && formData.targetRegions.length > 0) {
        regionScore = 60 + Math.min(formData.targetRegions.length * 8, 35)
      }
      
      // 根据风险偏好调整风险适配度
      if (formData.riskPreference) {
        const riskMap = {
          'high': 85,
          'medium': 65,
          'low': 45
        }
        riskScore = riskMap[formData.riskPreference] || 50
      }
      
      // 更新个人画像数据
      this.personalProfile = {
        studyAdaptability: Math.min(studyScore, 100),
        interestFit: Math.min(interestScore, 100),
        resourceMatch: Math.min(resourceScore, 100),
        regionFit: Math.min(regionScore, 100),
        riskFit: Math.min(riskScore, 100)
      }
    },
    handleReset () {
      // 重置个人画像数据
      this.personalProfile = {
        studyAdaptability: 50,
        interestFit: 50,
        resourceMatch: 50,
        regionFit: 50,
        riskFit: 50
      }
      this.updateRadarChart()
    },
    updateRadarChart () {
      if (!this.radarChart) return
      
      const option = {
        series: [{
          data: [
            {
              value: [
                this.personalProfile.studyAdaptability,
                this.personalProfile.interestFit,
                this.personalProfile.resourceMatch,
                this.personalProfile.regionFit,
                this.personalProfile.riskFit
              ],
              name: '个人画像',
              areaStyle: {
                color: 'rgba(59, 130, 246, 0.2)'
              },
              lineStyle: {
                color: 'rgba(59, 130, 246, 1)',
                width: 2
              },
              itemStyle: {
                color: 'rgba(59, 130, 246, 1)'
              }
            },
            {
              value: [
                this.currentMajor?.radarData?.studyAdaptability || 50,
                this.currentMajor?.radarData?.interestFit || 50,
                this.currentMajor?.radarData?.resourceMatch || 50,
                this.currentMajor?.radarData?.regionFit || 50,
                this.currentMajor?.radarData?.riskFit || 50
              ],
              name: this.currentMajor?.name || '专业名称',
              areaStyle: {
                color: 'rgba(239, 68, 68, 0.2)'
              },
              lineStyle: {
                color: 'rgba(239, 68, 68, 1)',
                width: 2,
                type: 'dashed'
              },
              itemStyle: {
                color: 'rgba(239, 68, 68, 1)'
              }
            }
          ]
        }]
      }
      
      this.radarChart.setOption(option)
    },
    handleMajorChange (majorId) {
      this.currentMajorId = majorId
      this.updateRadarChart()
    },
    handleAIMajorsUpdate (aiMajors) {
      // 接收AI推荐的专业并更新专业列表
      console.log('接收到AI推荐的专业:', aiMajors)
      this.majors = aiMajors
      // 更新当前选中的专业ID（默认选择第一个）
      this.currentMajorId = aiMajors.length > 0 ? aiMajors[0].id : 1
      this.updateRadarChart()
    },
    goToSankey () {
      this.$router.push('/sankey')
    },
    goBack () {
      // 返回上一级页面
      this.$router.go(-1)
    },
    handleDetailLoaded (detail) {
      // 处理专业详情加载完成的逻辑
      console.log('专业详情已加载:', detail)
    }
  }
}
</script>

<style lang="scss" scoped>
.radar-page {
  width: 100vw;
  height: 100vh;
  background: #f8fafc;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  position: relative;
}

.back-button-container {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
}

.back-btn {
  color: #666;
  font-size: 14px;
  
  &:hover {
    color: #409eff;
  }
}

.header-section {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
}

.radar-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}


.sankey-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
}

.legend-info {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: #475569;
}

.legend-box {
  width: 28px;
  height: 14px;
  border-radius: 2px;
  
  &.personal {
    background: rgba(59, 130, 246, 0.3);
    border: 2px solid rgba(59, 130, 246, 1);
  }
  
  &.major {
    background: rgba(239, 68, 68, 0.3);
    border: 2px dashed rgba(239, 68, 68, 1);
  }
}

.radar-container {
  flex: 1;
  width: 100%;
  position: relative;
}

.chart-wrapper {
  width: 100%;
  height: 100%;
}
</style>

